import { Directive, HostBinding, HostListener, Input, OnChanges } from '@angular/core';

import { ClipboardService, CopyKind } from '../services/clipboard.service';

/**
 * Kopiowanie przez kliknięcie w sam tekst — e-mail, telefon, IMEI wypisany w karcie.
 *
 * Dyrektywa, a nie komponent, bo te elementy już istnieją i niosą własne klasy
 * układu; opakowanie ich w komponent wstawiłoby dodatkowy węzeł do DOM i rozjechało
 * otaczający grid. Dla osobnej ikony obok wartości jest <app-copy-button>.
 *
 *   <span [appCopyOnClick]="user_info?.phone" copyKind="phone">{{ user_info?.phone }}</span>
 *
 * Gdy nie ma czego kopiować, dyrektywa jest całkowicie przezroczysta: nie udaje
 * elementu klikalnego i przepuszcza kliknięcie do rodzica.
 */
@Directive({ selector: '[appCopyOnClick]' })
export class CopyOnClickDirective implements OnChanges {

  /** Wartość trafiająca do schowka. */
  @Input('appCopyOnClick') value: string | number;

  /** Rodzaj danych; dobiera komunikat i podpowiedź z mapy w ClipboardService. */
  @Input() copyKind: CopyKind;

  @Input() copyMessage: string;

  @HostBinding('style.cursor') cursor: string = null;
  @HostBinding('attr.title') title: string = null;
  @HostBinding('attr.role') role: string = null;
  @HostBinding('attr.tabindex') tabindex: string = null;

  /**
   * Rozstrzygnięte przy zmianie wejść, nie przy każdym kliknięciu — a pytamy o to
   * przed serwisem, nie w nim: ClipboardService.copy() woła stopPropagation zanim
   * sprawdzi wartość, więc pusty tekst połykałby kliknięcie wiersza pod spodem.
   */
  private copyable = false;

  constructor(private clipboard: ClipboardService) {}

  ngOnChanges(): void {
    this.copyable = this.clipboard.isCopyable(this.value);
    this.cursor = this.copyable ? 'pointer' : null;
    this.role = this.copyable ? 'button' : null;
    this.tabindex = this.copyable ? '0' : null;
    this.title = this.copyable && this.copyKind ? this.clipboard.tooltipFor(this.copyKind) : null;
  }

  @HostListener('click', ['$event'])
  onClick(event: Event): void {
    if (!this.copyable) {
      return;
    }
    this.copy(event);
  }

  @HostListener('keydown.enter', ['$event'])
  @HostListener('keydown.space', ['$event'])
  onKeyboard(event: KeyboardEvent): void {
    if (!this.copyable) {
      return;
    }
    // Skoro spacja działa tu jak kliknięcie, nie może przy okazji przewinąć strony.
    event.preventDefault();
    this.copy(event);
  }

  private copy(event: Event): void {
    if (this.copyMessage) {
      this.clipboard.copy(this.value, this.copyMessage, event);
    } else {
      this.clipboard.copyAs(this.value, this.copyKind, event);
    }
  }
}
